import {
  Button,
  Positions,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  BarChart,
  BarChartSolid,
  Database,
  DatabaseSolid,
  SwapHorizontal,
  Time,
  TimeSolid,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { CustomDivider } from 'components/Layout/SectionHeader/SectionHeader';
import {
  HeaderSubText,
  HeaderText,
  HeaderTextWrapper,
  ShellNavBar,
  ShellNavWrapper,
} from 'components/NavBar/NavBar.styled';
import { NavBarProps } from 'components/NavBar/NavBar.types';
import { UserProfileButton } from 'components/NavBar/UserProfileButton';
import { Permissions } from 'context/constants';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { AppState, useStore } from 'store';
import {
  checkAvailabilityEnabled,
  checkOperationsEnabled,
} from 'utils/features';
import { extractFirstSegmentFromUrl } from 'utils/url';

export const getDefaultPathForUser = (permissions?: Permissions[]) => {
  if (permissions?.includes(Permissions.VIEW_OPTIMISATION)) {
    return 'optimisation';
  }
  if (permissions?.includes(Permissions.EDIT_AVAILABILITY)) {
    return 'availability';
  }
  return 'optimisation';
};

export const routerMenuItems = (
  currentTab: string,
  optimisationEnabled: boolean,
  operationsEnabled: boolean,
  availabilityEnabled: boolean,
) => {
  const items = [];

  if (optimisationEnabled) {
    items.push({
      key: '1',
      label: (
        <Text size={Sizes.Small} bold={currentTab === 'optimisation'}>
          Optimisation
        </Text>
      ),
      path: `optimisation`,
      itemIcon:
        currentTab === 'optimisation' ? <BarChartSolid /> : <BarChart />,
    });
  }

  if (operationsEnabled) {
    items.push({
      key: '2',
      label: (
        <Text size={Sizes.Small} bold={currentTab === 'operations'}>
          Operations
        </Text>
      ),
      path: `operations`,
      itemIcon: currentTab === 'operations' ? <DatabaseSolid /> : <Database />,
    });
  }

  if (availabilityEnabled) {
    items.push({
      key: '3',
      label: (
        <Text size={Sizes.Small} bold={currentTab === 'availability'}>
          Availability
        </Text>
      ),
      path: `availability`,
      itemIcon: currentTab === 'availability' ? <TimeSolid /> : <Time />,
    });
  }

  if (items.length === 1) {
    return [];
  }

  return items;
};

const CompanySwitch = ({
  company,
  multipleCompanies,
  companyName,
  setCompanyModalOpen,
}: {
  company: string | undefined;
  multipleCompanies: boolean;
  companyName: string | undefined;
  setCompanyModalOpen: () => void;
}) => {
  if (company && multipleCompanies) {
    return (
      <Button
        size={Sizes.Small}
        icon={<SwapHorizontal />}
        iconPosition={Positions.Right}
        variant="transparent"
        data-testid="navbar-modal-button"
        onClick={setCompanyModalOpen}
      >
        {companyName}
      </Button>
    );
  }

  if (!multipleCompanies) {
    return (
      <HeaderText data-testid="navbar-company-name-header">
        {companyName}
      </HeaderText>
    );
  }
  return null;
};

export const NavBar = ({
  multipleCompanies,
  companyName,
  companyError,
}: NavBarProps) => {
  const { company } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const currentTab = useMemo(() => {
    return extractFirstSegmentFromUrl(location.pathname);
  }, [location]);

  const [setCompanyModalOpen] = useStore((state: AppState) => [
    state.setCompanyModalOpen,
    state.companyModalOpen,
  ]);

  const { permissions } = useAppUserDetailsFromJwt<Permissions>();
  const defaultPath = getDefaultPathForUser(permissions);

  const hasViewOptimisationPermission =
    permissions?.includes(Permissions.VIEW_OPTIMISATION) ?? false;
  const hasEditAvailabilityPermission =
    permissions?.includes(Permissions.EDIT_AVAILABILITY) ?? false;
  const enableOptimisation = hasViewOptimisationPermission;
  const enableOperations = checkOperationsEnabled();
  const enableAvailability =
    checkAvailabilityEnabled() && hasEditAvailabilityPermission;

  return (
    <ShellNavWrapper>
      <ShellNavBar
        size={Sizes.Large}
        data-testid="shell-navbar"
        onSelect={(event: any) => {
          void navigate(`${company}/${event.path}`);
        }}
        items={routerMenuItems(
          currentTab ?? defaultPath,
          enableOptimisation,
          enableOperations,
          enableAvailability,
        )}
        selectedPath={currentTab ?? defaultPath}
        header={
          <>
            <HeaderTextWrapper>
              <HeaderSubText>Shell Energy</HeaderSubText>
              <HeaderText>Battery Management Hub</HeaderText>
            </HeaderTextWrapper>
          </>
        }
        avatarArea={
          <>
            {companyError ? null : (
              <CompanySwitch
                company={company}
                multipleCompanies={multipleCompanies}
                companyName={companyName}
                setCompanyModalOpen={setCompanyModalOpen}
              />
            )}
            <UserProfileButton />
          </>
        }
      ></ShellNavBar>
      <CustomDivider prominence={'subtle'} size={Sizes.Small} />
    </ShellNavWrapper>
  );
};
